import { storage, auth } from '../config/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://us-central1-the-random-store-50667.cloudfunctions.net/api'
  : 'http://localhost:5001/the-random-store-50667/us-central1/api';

// Upload STL file to Firebase Storage
export const uploadSTLFile = async (file, progressCallback) => {
  try {
    // Create a unique filename with timestamp
    const timestamp = Date.now();
    const fileName = `stl-files/${timestamp}_${file.name}`;
    const storageRef = ref(storage, fileName);

    // Upload the file
    const uploadTask = await uploadBytes(storageRef, file);
    
    // Get the download URL
    const downloadURL = await getDownloadURL(uploadTask.ref);
    
    return {
      success: true,
      url: downloadURL,
      fileName: file.name,
      storagePath: fileName
    };
  } catch (error) {
    console.error('Error uploading STL file:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Submit quote request
export const submitQuoteRequest = async (quoteData) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated');
    }

    const token = await user.getIdToken();
    
    const response = await fetch(`${API_BASE_URL}/quotes/request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(quoteData)
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to submit quote request');
    }

    return data;
  } catch (error) {
    console.error('Error submitting quote request:', error);
    throw error;
  }
};

// Get user's quote requests
export const getUserQuotes = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated');
    }

    const token = await user.getIdToken();
    
    const response = await fetch(`${API_BASE_URL}/quotes/my-quotes`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch quotes');
    }

    return data.quotes;
  } catch (error) {
    console.error('Error fetching user quotes:', error);
    throw error;
  }
};

// Get quote by ID
export const getQuoteById = async (quoteId) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated');
    }

    const token = await user.getIdToken();
    
    const response = await fetch(`${API_BASE_URL}/quotes/${quoteId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch quote');
    }

    return data.quote;
  } catch (error) {
    console.error('Error fetching quote:', error);
    throw error;
  }
};

// Admin functions
export const getAdminQuotes = async (status = 'all', limit = 50, offset = 0) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated');
    }

    const token = await user.getIdToken();
    
    const params = new URLSearchParams({
      status,
      limit: limit.toString(),
      offset: offset.toString()
    });
    
    const response = await fetch(`${API_BASE_URL}/quotes/admin/all?${params}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch admin quotes');
    }

    return data.quotes;
  } catch (error) {
    console.error('Error fetching admin quotes:', error);
    throw error;
  }
};

export const updateQuoteStatus = async (quoteId, updateData) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated');
    }

    const token = await user.getIdToken();
    
    const response = await fetch(`${API_BASE_URL}/quotes/admin/${quoteId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(updateData)
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to update quote');
    }

    return data;
  } catch (error) {
    console.error('Error updating quote:', error);
    throw error;
  }
};

// Create order from quote
export const createOrderFromQuote = async (quoteId, orderData) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated');
    }

    const token = await user.getIdToken();
    
    const response = await fetch(`${API_BASE_URL}/quotes/${quoteId}/create-order`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(orderData)
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to create order from quote');
    }

    return data;
  } catch (error) {
    console.error('Error creating order from quote:', error);
    throw error;
  }
};
